import { memo, useId, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { SQUARES } from 'chess.js';
import { proxy, useSnapshot } from 'valtio';

import { FEN_STARTING_POSITION } from '@libs/chess/constants';
import { parseFen } from '@libs/chess/parseFen';
import { cx } from '@libs/classnames';
import { ContentEditableText } from '@ui/components/ContentEditableText/ContentEditableText';

import { Button, Label, PositionEditor } from '@features/game/ui/PopupControls';
import stylesSettings from '@features/game/ui/PopupControls/PopupControls.module.css';

import styles from './simulator.module.css';

const HEARTBEAT_ENABLED = false;

export const Route = createFileRoute('/boards/simulator')({
  component: RouteComponent,
});

function RouteComponent() {
  const [store] = useState(() => new Store());
  const snap = useSnapshot(store.state);
  const notConfigured = !snap.secret.trim() || !snap.serial.trim() || !snap.api.trim();

  return (
    <div className={cx(styles.page, styles.settings, 'theme-dark')}>
      <div className={styles.config}>
        <div>
          <Label>API Endpoint</Label>
          <Input initialValue={snap.api} onChange={store.onApiChange} />
        </div>
        <div>
          <Label>Серийный номер доски</Label>
          <Input initialValue={snap.serial} onChange={store.onSerialChahge} />
        </div>
        <div>
          <Label>Секрет</Label>
          <Input initialValue={snap.secret} onChange={store.onSecretChahge} />
        </div>
        <div>
          <Label>Время</Label>
          <div className={styles.time}>
            <Input initialValue={snap.whiteSec} onChange={store.onWhiteChange} postfix=" сек (w)" />
            <Input initialValue={snap.blackSec} onChange={store.onBlackChange} postfix=" сек (b)" />
          </div>
        </div>
      </div>
      <PositionEditor isMobile={false} fen={snap.fen} onChange={store.onFenChahge} noValidate />
      <Button onClick={store.onSubmit} disabled={notConfigured || snap.sumbitting}>
        {!snap.sumbitting && 'Отправить'}
        {snap.sumbitting && 'Отправляем...'}
      </Button>
      {HEARTBEAT_ENABLED && (
        <Button disabled={notConfigured || snap.sumbittingHeartbeat}>
          {!snap.sumbittingHeartbeat && 'Отправить Heartbeat'}
          {snap.sumbittingHeartbeat && 'Отправляем...'}
        </Button>
      )}
      {snap.error}
    </div>
  );
}

export const Input = memo(function Input({
  initialValue,
  notActive,
  inputMode,
  prefix,
  postfix,
  onClick,
  onChange,
}: {
  initialValue: string;
  notActive?: boolean;
  prefix?: string;
  postfix?: string;
  inputMode?: 'numeric' | 'text';
  onClick?: () => void;
  onChange?: (v: string) => void;
}) {
  const id = useId();

  return (
    <div
      className={cx({
        [stylesSettings.is_round]: true,
        [stylesSettings.is_right]: true,
        [stylesSettings.is_blurry]: notActive,
        [stylesSettings.is_opaque]: !notActive,
        [stylesSettings.is_clickable]: notActive,
      })}
      onClick={() => {
        if (!notActive) {
          document.getElementById(id)?.focus();
        }
        onClick?.();
      }}
    >
      {prefix}{' '}
      <ContentEditableText
        id={id}
        inputMode={inputMode}
        className={stylesSettings.input__editable}
        initialValue={initialValue}
        onChange={onChange}
      />
      {postfix && <>&nbsp;{postfix}</>}
    </div>
  );
});

class State {
  fen?: string = FEN_STARTING_POSITION;
  api: string = '';
  serial: string = '';
  secret: string = '';
  sumbitting = false;
  sumbittingHeartbeat = false;
  error?: string;

  whiteSec = '60';
  blackSec = '60';
}

class Store {
  constructor() {
    this.state = proxy(new State());
  }

  state: State;

  onFenChahge = (fen?: string) => {
    this.state.fen = fen;
  };

  onApiChange = (v: string) => {
    this.state.api = v;
  };

  onSerialChahge = (v: string) => {
    this.state.serial = v;
  };

  onSecretChahge = (v: string) => {
    this.state.secret = v;
  };

  onWhiteChange = (v: string) => {
    this.state.whiteSec = v;
  };

  onBlackChange = (v: string) => {
    this.state.blackSec = v;
  };

  onSubmit = async () => {
    this.state.sumbitting = true;
    this.state.error = undefined;
    try {
      const parsed = parseFen(this.state.fen);
      const side = parsed ? (parsed.color === 'white' ? 'White' : 'Black') : 'Unknown';
      const fenPositions = parsed?.board ?? {};
      const positions: Array<{ square: string; piece: string }> = [];
      SQUARES.forEach((square) => {
        const piece = fenPositions[square];
        if (piece) {
          positions.push({
            square: square.toUpperCase(),
            piece: piece[0] === 'w' ? piece[1]! : piece[1]!.toLowerCase(),
          });
        }
      });

      const payload = JSON.stringify({
        positions,
        timestamp: Date.now(),
        clock: {
          whiteLeftMs: parseInt(this.state.whiteSec.trim() || '0', 10) * 1000,
          blackLeftMs: parseInt(this.state.blackSec.trim() || '0', 10) * 1000,
          side,
          state: 'Unknown',
        },
        idempotencyKey: `simulator-${Date.now()}`,
      });

      const signature = await computeHash(this.state.secret, payload);

      const result = await fetch(`${this.state.api}/api/v1/${this.state.serial}/move`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          signature,
        },
        body: payload,
      });

      if (result.status !== 200) {
        this.state.error = `Ошибка вызова, статус ${result.status}`;
      }
    } catch (e) {
      this.state.error = (e as Error).message;
    } finally {
      this.state.sumbitting = false;
    }
  };

  onHeartbeat = async () => {
    this.state.sumbittingHeartbeat = true;
    try {
      // nothing
    } finally {
      this.state.sumbittingHeartbeat = false;
    }
  };
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return window.btoa(binary);
}

async function computeHash(secret: string, data: string) {
  const encoder = new TextEncoder();
  const secretBytes = encoder.encode(secret);
  const dataBytes = encoder.encode(data);

  const key = await crypto.subtle.importKey(
    'raw',
    secretBytes,
    {
      name: 'HMAC',
      hash: { name: 'SHA-512' },
    },
    false,
    ['sign'],
  );

  const signature = await crypto.subtle.sign('HMAC', key, dataBytes);

  return arrayBufferToBase64(signature);
}
